import React, { useEffect } from "react";

import {
    TextField,
    FormGroup,
    Typography,
    Link,
    Select,
    FormControl,
    InputLabel,
    FormHelperText
} from "@material-ui/core";
import { apiPost } from "../services/api";
import TracercoButton from "./shared/TracercoButton";
import { isValidEmail } from "../services/validators";
import { useTranslation } from "react-i18next";

interface ThankYouFormProps {
    Resend(): void;
    firstName: string;
}

const ThankYouForm: React.FC<ThankYouFormProps> = props => {
    const { t } = useTranslation();
    const firstName = props.firstName;
    return (
        <React.Fragment>
            <br />
            <Typography variant="h4">
                {t("demo-request-thank-you-title")}
            </Typography>
            <Typography variant="body1">
                We appreciate you contacting us {firstName} for a demo of the
                Tracerco Reservoir Insights platform. One of our colleagues will
                get back in touch with you soon!
            </Typography>
            <br />
            <Typography variant="h4">Contact Us</Typography>
            <Typography variant="body1">
                In the meantime, if you have any questions you can contact us
                via&nbsp;
                <Link
                    color="inherit"
                    href="mailto:tracerco.software@tracerco.com"
                    underline="always"
                >
                    email
                </Link>
                .
            </Typography>
        </React.Fragment>
    );
};

const DemoRequestForm: React.FC = () => {
    const { t } = useTranslation();

    const regions = [
        "Africa",
        "Americas",
        "Asia",
        "Australia",
        "Caspian",
        "China",
        "Europe",
        "Middle East",
        "Norway",
        "UK",
        "Other"
    ];

    const [firstName, setFirstName] = React.useState<string>("");
    const [lastName, setLastName] = React.useState<string>("");
    const [jobTitle, setJobTitle] = React.useState<string>("");
    const [company, setCompany] = React.useState<string>("");
    const [email, setEmail] = React.useState<string>("");
    const [phone, setPhone] = React.useState<string>("");
    const [region, setRegion] = React.useState<string>("");

    const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);

    const resetForm = () => {
        setFirstName("");
        setLastName("");
        setJobTitle("");
        setCompany("");
        setEmail("");
        setPhone("");
        setRegion("");

        setFirstNameError(false);
        setLastNameError(false);
        setEmailError(false);
        setRegionError(false);
    };

    const [firstNameError, setFirstNameError] = React.useState(false);
    const [lastNameError, setLastNameError] = React.useState(false);
    const [emailError, setEmailError] = React.useState(false);
    const [regionError, setRegionError] = React.useState(false);

    const validateFirstName = value => {
        const invalid = Required(value);

        setFirstNameError(invalid);

        return invalid;
    };

    const onFirstNameChange = event => {
        setFirstName(event.target.value);

        validateFirstName(event.target.value);
    };

    const validateLastName = value => {
        const invalid = Required(value);

        setLastNameError(invalid);

        return invalid;
    };

    const onLastNameChange = event => {
        setLastName(event.target.value);

        validateLastName(event.target.value);
    };

    const onJobTitleChange = event => {
        setJobTitle(event.target.value);
    };

    const onCompanyChange = event => {
        setCompany(event.target.value);
    };

    const validateEmail = value => {
        const invalid = !isValidEmail(value);

        setEmailError(invalid);

        return invalid;
    };

    const onEmailChange = event => {
        setEmail(event.target.value);
        validateEmail(event.target.value);
    };

    const onPhoneChange = event => {
        setPhone(event.target.value);
    };

    const validateRegion = value => {
        const invalid = regions.indexOf(value) === -1;

        setRegionError(invalid);

        return invalid;
    };

    const onRegionInputChange = (
        event: React.ChangeEvent<{ name?: string; value: unknown }>
    ) => {
        setRegion(event.target.value as string);
        validateRegion(event.target.value as string);
    };

    function Required(value) {
        return value === "";
    }

    function makeUndefined(value) {
        if (value) {
            return value;
        }

        return undefined;
    }

    const [isValid, setIsValid] = React.useState(false);

    useEffect(() => {
        setIsValid(
            !Required(firstName) &&
                !Required(lastName) &&
                !validateEmail(email) &&
                !Required(region)
        );
    }, [firstName, lastName, email, region]);

    const isFormInvalid = () => {
        const invalidFirstName = validateFirstName(firstName);
        const invalidLastName = validateLastName(lastName);
        const invalidEmail = validateEmail(email);
        const invalidRegion = validateRegion(region);

        const invalid =
            invalidFirstName ||
            invalidLastName ||
            invalidEmail ||
            invalidRegion;

        return invalid;
    };

    const onSubmitClick = async () => {
        const invalid = isFormInvalid();

        if (!invalid) {
            await apiPost("demo-request", {
                firstName: makeUndefined(firstName),
                lastName: makeUndefined(lastName),
                jobTitle: makeUndefined(jobTitle),
                company: makeUndefined(company),
                email: makeUndefined(email),
                phone: makeUndefined(phone),
                region: makeUndefined(region)
            });

            setIsSubmitted(true);

            resetForm();
        }
    };

    return (
        <React.Fragment>
            {!isSubmitted ? (
                <FormGroup>
                    <TextField
                        value={firstName}
                        fullWidth
                        label={t("demo-request-first-name")}
                        required
                        error={firstNameError}
                        onChange={onFirstNameChange}
                        inputProps={{
                            autoComplete: "given-name"
                        }}
                    />
                    <TextField
                        value={lastName}
                        fullWidth
                        label={t("demo-request-last-name")}
                        required
                        error={lastNameError}
                        onChange={onLastNameChange}
                        inputProps={{
                            autoComplete: "family-name"
                        }}
                    />

                    <TextField
                        value={company}
                        fullWidth
                        inputProps={{
                            autoComplete: "organization"
                        }}
                        label={t("demo-request-company")}
                        onChange={onCompanyChange}
                    />
                    <TextField
                        value={jobTitle}
                        fullWidth
                        inputProps={{
                            autoComplete: "organization-title"
                        }}
                        label={t("demo-request-job-title")}
                        onChange={onJobTitleChange}
                    />
                    <TextField
                        value={email}
                        fullWidth
                        required
                        inputProps={{
                            autoComplete: "email"
                        }}
                        label={t("demo-request-email")}
                        error={emailError}
                        onChange={onEmailChange}
                    />
                    <TextField
                        value={phone}
                        fullWidth
                        label={t("demo-request-phone")}
                        onChange={onPhoneChange}
                        inputProps={{
                            autoComplete: "tel"
                        }}
                    />

                    <FormControl error={regionError}>
                        <InputLabel required htmlFor="name-native-error">
                            {t("demo-request-region")}
                        </InputLabel>

                        <Select
                            required
                            native
                            value={region}
                            onChange={onRegionInputChange}
                        >
                            <option
                                aria-label="None"
                                value=""
                                disabled
                            ></option>

                            {regions.map(region => {
                                return (
                                    <option key={region} value={region}>
                                        {region}
                                    </option>
                                );
                            })}
                        </Select>
                        <FormHelperText></FormHelperText>
                    </FormControl>

                    <TracercoButton
                        variant="contained"
                        disabled={!isValid}
                        onClick={onSubmitClick}
                    >
                        {t("demo-request-submit")}
                    </TracercoButton>
                    <Typography
                        variant="subtitle2"
                        align="left"
                        component="div"
                    >
                        By submitting the form you agree to our &nbsp;
                        <Link
                            underline="always"
                            href="https://www.tracerco.com/privacy-notice"
                        >
                            Privacy notice,
                        </Link>
                        &nbsp; and our &nbsp;
                        <Link
                            underline="always"
                            href="https://www.tracerco.com/terms-of-use/"
                        >
                            Terms of use.
                        </Link>
                        &nbsp;
                    </Typography>
                </FormGroup>
            ) : (
                <ThankYouForm
                    firstName={firstName}
                    Resend={() => setIsSubmitted(false)}
                />
            )}
        </React.Fragment>
    );
};

export default DemoRequestForm;
